<template>
  <utd-modal-dialog ref="forgotPasswordRef"
                    :modal-classes="'reset-password-modal'"
                    :dialog-classes="'ds1-default-dialog'"
                    :close-on-overlay-click="isMobileOnDesktop"
                    :close-on-escape="!isMobileOnDesktop"
                    @closed="handleClose()">
    <template #header>
      <span v-show="resetPwdDisplayShowEmailCtrls">{{ $t('LOGIN.RESET_PASSWORD_INTRO') }}</span>
      <span v-show="!resetPwdDisplayShowEmailCtrls">
        {{ $t('LOGIN.RESET_PASSWORD_EMAIL_SENT') }}</span>
    </template>
    <template v-slot>
      <div id="forgotPasswordView" v-bind="$attrs">
        <div v-if="isDisasterRecovery" class="dialog__bcdr">
          <p>{{ disasterExplanation }}</p>
          <p>{{ disasterApology }}</p>
        </div>
        <div v-else class="dialog__content">
          <p v-show="resetPwdDisplayShowEmailCtrls"
             class="dialog__instructions-info"
             v-text="$t('LOGIN.RESET_PASSWORD_INSTRUCTIONS')" />

          <form v-show="resetPwdDisplayShowEmailForm"
                ref="forgotPasswordForm"
                name="forgotPasswordForm"
                :class="{isActive: resetPwdIsSendEmailActive}"
                @submit.prevent="sendEmailAddressRequest">
            <fieldset class="dialog__fieldset">

              <utd-notification v-if="resetPwdMessageCode"
                                :notification-mode="'error'">
                <template v-slot:title>{{ $t(resetPwdMessageCode) }}</template>
                <span class="notification-text">{{ $t('MYACCOUNT.ACCOUNT_GUID_MESSAGE') }}</span>
              </utd-notification>

              <utd-notification v-if="!resetPwdMessageCode && resetPwdMessageContent"
                                id="informationBox">
                <template v-slot:title>Information</template>
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span class="notification-text" v-html="resetPwdMessageContent" />
              </utd-notification>

              <div v-show="resetPwdDisplayShowEmailCtrls" class="dialog__input-container wk-js2">
                <utd-text-input :label="$t('LOGIN.EMAIL_ADDRESS')"
                                input-id="emailAddress"
                                :is-error="hasEmailError"
                                :error-message="$t('MYACCOUNT.INVALID_EMAIL')"
                                :required="true">
                  <input id="emailAddress"
                         v-model.trim="emailFormModel"
                         type="email"
                         class="wk-field-input"
                         required
                         @input="resetEmailValidation()">
                </utd-text-input>
              </div>
            </fieldset>
          </form>
          <utd-customer-support />
        </div>
      </div>
    </template>
    <template #footer>
      <utd-button v-show="submitted"
                  id="closeButton"
                  class="form-button login-button close-button"
                  @click="closeDialog()">
        {{ $t('CLOSE') }}
      </utd-button>
      <utd-button v-show="!submitted"
                  id="cancelButton"
                  class="cancel-text-button"
                  button-style="text"
                  @click="closeDialog()">
        {{ $t('CANCEL') }}
      </utd-button>
      <utd-button v-show="resetPwdDisplayShowEmailCtrls && !isDisasterRecovery"
                  id="btnResetRequest"
                  :disabled="isSubmitDisabled"
                  class="form-button login-button request-button"
                  :class="{isActive: resetPwdIsSendEmailActive}"
                  @click.prevent="submitForm">
        {{ $t('LOGIN.SEND_REQUEST') }}
      </utd-button>
    </template>
  </utd-modal-dialog>
</template>

<script>
import { C_UI, BCDR_MESSAGE } from '_acaSrc/utility/constants';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import { getWindow } from '_acaSrc/utility/DOM';
import { dispatchEvent } from '_acaSrc/utility/Events';
import { safeTimeout } from '_acaSrc/utility/timers';
import UtdModalDialog from '_acaSrc/components/shared/stdlib/UtdModalDialog.vue';
import UtdButton from '_acaSrc/components/shared/stdlib/UtdButton.vue';
import UtdTextInput from '_acaSrc/components/shared/input/UtdTextInput.vue';
import UtdNotification from '_acaSrc/components/shared/utd/UtdNotification.vue';
import UtdCustomerSupport from '_acaSrc/components/shared/stdlib/UtdCustomerSupport.vue';
import EmailUtils from '_acaSrc/utility/Email.js';

import {
    SET_RESET_PWD_MESSAGE_CONTENT,
    SET_RESET_PWD_EMAIL_FORM,
    SET_RESET_PWD_IS_SEND_EMAIL_DISABLED,
    SET_RESET_PWD_IS_SEND_EMAIL_ACTIVE,
    RESET_EMAIL_FORM
} from '_acaSrc/store/auth.store';

export default {
    components: {
        UtdNotification,
        UtdModalDialog,
        UtdButton,
        UtdTextInput,
        UtdCustomerSupport
    },
    emits: [ 'close-forgot-password-modal' ],
    data() {
        return {
            cancelResizeTimeout: null,
            disasterExplanation: BCDR_MESSAGE.DEFAULT,
            disasterApology: BCDR_MESSAGE.APOLOGY,
            hasEmailError: false,
            submitted: false
        };
    },
    computed: {
        ...mapGetters('app', [ 'countryCode', 'isDisasterRecovery' ]),
        ...mapGetters('auth', [
            'resetPwdEmailForm',
            'resetPwdMessageCode',
            'resetPwdMessageContent',
            'resetPwdDisplayShowEmailForm',
            'resetPwdDisplayShowEmailCtrls',
            'resetPwdIsSendEmailDisabled',
            'resetPwdIsSendEmailActive'
        ]),
        ...mapGetters('device', [ 'isDesktopView', 'isMobileOnDesktop' ]),
        emailFormModel: {
            get() {
                return this.resetPwdEmailForm.email;
            },
            set(value) {
                this[SET_RESET_PWD_EMAIL_FORM](value);
            }
        },
        isSubmitDisabled() {
            return this.resetPwdIsSendEmailDisabled 
                || this.resetPwdEmailForm.email === '';
        }
    },
    methods: {
        ...mapActions('auth', [
            'sendResetRequestEmailAddress'
        ]),
        ...mapMutations('auth', [
            RESET_EMAIL_FORM,
            SET_RESET_PWD_MESSAGE_CONTENT,
            SET_RESET_PWD_EMAIL_FORM,
            SET_RESET_PWD_IS_SEND_EMAIL_DISABLED,
            SET_RESET_PWD_IS_SEND_EMAIL_ACTIVE
        ]),
        triggerDelayedResizeEvent() {
            this.cancelResizeTimeout = safeTimeout(() => {
                dispatchEvent('resize');
            }, C_UI.WINDOW_RESIZE_DELAY_MS, { cancelHook: true }).cancelTimeout;
        },
        sendEmailAddressRequest() {
            if (this.resetPwdEmailForm.email === '' || this.resetPwdEmailForm.email === undefined) {
                this[SET_RESET_PWD_MESSAGE_CONTENT](this.$t('LOGIN.RESET_PASSWORD_ERROR'));
            }
            else {
                this[SET_RESET_PWD_IS_SEND_EMAIL_DISABLED](true);
                this[SET_RESET_PWD_IS_SEND_EMAIL_ACTIVE](true);
                // Hide keyboard
                const focus = getWindow().document.querySelector(':focus');
                if (focus) {
                    focus.blur();
                }
                this.sendResetRequestEmailAddress().finally(() => {
                    this[SET_RESET_PWD_IS_SEND_EMAIL_DISABLED](false);
                    this[SET_RESET_PWD_IS_SEND_EMAIL_ACTIVE](false);
                    this.submitted = true;
                    // Trigger a window resize to ensure footer repositions
                    this.triggerDelayedResizeEvent();
                });
            }
        },
        open() {
            this.$refs.forgotPasswordRef.open();
        },
        async closeDialog() {
            this.$refs.forgotPasswordRef.close();
        },
        handleClose() {
            this[RESET_EMAIL_FORM]();
            this.resetEmailValidation();
            this.submitted = false;
            this.$emit('close-forgot-password-modal');
        },
        resetEmailValidation() {
            this.hasEmailError = false;
        },
        isValidEmail(email) {
            return EmailUtils.isValidEmail(email);
        },
        submitForm() {
            if(this.isValidEmail(this.resetPwdEmailForm.email)) {
                this.$refs
                    .forgotPasswordForm
                    .dispatchEvent(new Event('submit', {cancelable: true}));
            } 
            else {
                this.hasEmailError = true;
            }
        }
    }
};
</script>

<style lang="less">
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

.reset-password-modal .utd-dialog-content {
  .ds1-pa-4();
}

.xAIMobile .reset-password-modal {
  .xai-mobile-styles();

  .cancel-text-button {
    color: @WKCE-CREAM-SHADE2;
  }

  .request-button, .close-button {
    background-color: @WKCE-CREAM-SHADE1;
    border-color: @WKCE-CREAM-SHADE1;
  }
}

@media screen and (min-width: 768px) {
  .reset-password-modal {
    .utd-modal-content .utd-dialog-frame {
      width: 670px;
    }
  }
}

@media screen and (max-width: 768px) {
  .reset-password-modal {
    .utd-modal-content .utd-dialog-frame {
      width: 100%;
    }

    .dialog__fieldset {
      min-width: 0;
    }

    #informationBox {
      .ds1-pl-2();
      .ds1-pr-1();
      
      .utd-notification-title {
        .ds1-pl-3();
      }

      .notification-text {
        ul, ol {
          .ds1-ml-0();
        }
      }
    }
  }
}
</style>

<style lang="less" scoped>
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

.dialog__instructions-info {
  .ds1-reset-p();
  .ds1-mb-2();
}

.dialog__content {
  font-size: 14px;

  #informationBox {
    .ds1-mt-0();
    .ds1-mb-3();
  }
}

.dialog__fieldset {
  .ds1-ma-0();
  .ds1-pa-0();
  border: none;
}

.cancel-text-button {
  font-size: 0.8em;
  
  &:hover {
    text-decoration: underline;
  }
}

</style>